/**
 * PhotoViewerPage - Full-screen photo viewer with navigation
 *
 * Displays a photo at full resolution with caption, uploader info,
 * and navigation controls. Supports keyboard navigation.
 */
import { useEffect, useCallback, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  useMediaDetail,
  useMediaChildTags,
  useAvailableChildren,
  useUpdateMediaChildTags,
  useUpdateMedia,
  useDeleteMedia,
  useLoveStatus,
  useToggleLove,
  useAccessGroups,
} from '@wovendays/api-client';
import { useTranslation } from '@wovendays/i18n';
import { VideoPlayer } from '@/components/VideoPlayer';
import { useAuth } from '@/contexts/auth';
import { CommentSheet } from '@/components/CommentSheet';
import { ReactionBar } from '@/components/ReactionBar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { X, Trash2, ChevronLeft, ChevronRight, MapPin, Pencil, Check, Calendar, Download, RotateCw, Heart, Lock } from 'lucide-react';

interface ViewerLocationState {
  familyId?: string;
  childFilter?: string | null;
  mediaIds?: string[];
}

export function PhotoViewerPage() {
  const { t } = useTranslation('photo');
  const { id: mediaId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const state = location.state as ViewerLocationState | null;
  const familyId = state?.familyId;
  const mediaIds = state?.mediaIds;

  // Navigation state
  const currentIndex = mediaIds && mediaId ? mediaIds.indexOf(mediaId) : -1;
  const hasPrev = currentIndex > 0;
  const hasNext = mediaIds ? currentIndex < mediaIds.length - 1 : false;

  // Touch gesture refs
  const touchStartRef = useRef<{ x: number; y: number; time: number } | null>(null);
  const lastTapRef = useRef<number>(0);
  const [showHeartAnimation, setShowHeartAnimation] = useState(false);

  const { data, isLoading } = useMediaDetail(mediaId);

  const media = data?.media;
  const resolvedFamilyId = media?.familyId || familyId;

  const { data: loveData } = useLoveStatus(mediaId);
  const { toggle: toggleLove } = useToggleLove(mediaId);
  const loveCount = loveData?.loveCount ?? 0;
  const userLoved = loveData?.userLoved ?? false;

  // Access groups for restrict toggle (only fetched for admin/owner)
  const { data: accessGroupsData } = useAccessGroups(
    data?.media?.viewerRole === 'owner' || data?.media?.viewerRole === 'admin' ? resolvedFamilyId : undefined
  );
  const fullAccessGroup = accessGroupsData?.accessGroups?.[0];

  const { data: childTagsData } = useMediaChildTags(mediaId, !!media);
  const { data: availableChildrenData } = useAvailableChildren(resolvedFamilyId, !!media);

  const currentTags = childTagsData?.children ?? [];
  const availableChildren = availableChildrenData?.children ?? [];
  const currentTagIds = new Set(currentTags.map(t => t.id));

  const tagMutation = useUpdateMediaChildTags(mediaId, resolvedFamilyId);

  const handleToggleChild = (childId: string) => {
    const newIds = currentTagIds.has(childId)
      ? currentTags.filter(t => t.id !== childId).map(t => t.id)
      : [...currentTags.map(t => t.id), childId];
    tagMutation.mutate(newIds);
  };

  // Caption editing state
  const [editingCaption, setEditingCaption] = useState(false);
  const [captionDraft, setCaptionDraft] = useState('');

  const updateMediaMutation = useUpdateMedia(mediaId, familyId);
  const deleteMediaMutation = useDeleteMedia(mediaId, familyId);

  const captionMutation = {
    mutate: (caption: string | null) => {
      updateMediaMutation.mutate({ caption }, { onSuccess: () => setEditingCaption(false) });
    },
    isPending: updateMediaMutation.isPending,
  };

  // Date editing state
  const [editingDate, setEditingDate] = useState(false);
  const [dateDraft, setDateDraft] = useState('');

  const dateMutation = {
    mutate: (takenAt: number | null) => {
      updateMediaMutation.mutate({ takenAt }, { onSuccess: () => setEditingDate(false) });
    },
    isPending: updateMediaMutation.isPending,
  };

  const rotateMutation = {
    mutate: (rotation: number) => {
      updateMediaMutation.mutate({ rotation });
    },
    isPending: updateMediaMutation.isPending,
  };

  const deleteMutation = {
    mutate: () => {
      deleteMediaMutation.mutate(undefined, { onSuccess: () => navigate(-1) });
    },
    isPending: deleteMediaMutation.isPending,
  };

  const handleClose = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const goToPrev = useCallback(() => {
    if (!hasPrev || !mediaIds) return;
    navigate(`/photo/${mediaIds[currentIndex - 1]}`, { state, replace: true });
  }, [hasPrev, mediaIds, currentIndex, state, navigate]);

  const goToNext = useCallback(() => {
    if (!hasNext || !mediaIds) return;
    navigate(`/photo/${mediaIds[currentIndex + 1]}`, { state, replace: true });
  }, [hasNext, mediaIds, currentIndex, state, navigate]);

  // Keyboard navigation
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      // Don't navigate when typing in comment input
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if (e.key === 'Escape') handleClose();
      if (e.key === 'ArrowLeft') goToPrev();
      if (e.key === 'ArrowRight') goToNext();
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleClose, goToPrev, goToNext]);

  // No speculative preload of adjacent full-res images — each CDN delivery is billed.
  // The SW cache means revisiting a photo is instant after the first view.

  const triggerLoveAnimation = useCallback(() => {
    if (!userLoved) toggleLove(false);
    setShowHeartAnimation(true);
    setTimeout(() => setShowHeartAnimation(false), 800);
  }, [userLoved, toggleLove]);

  // Touch gesture handlers
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    if (e.touches.length === 1 && touch) {
      touchStartRef.current = { x: touch.clientX, y: touch.clientY, time: Date.now() };
    }
  }, []);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    const touch = e.changedTouches[0];
    if (!touchStartRef.current || e.changedTouches.length !== 1 || !touch) return;
    const start = touchStartRef.current;
    const end = { x: touch.clientX, y: touch.clientY };
    const elapsed = Date.now() - start.time;
    touchStartRef.current = null;

    const deltaX = end.x - start.x;
    const deltaY = end.y - start.y;
    const absDeltaX = Math.abs(deltaX);
    const absDeltaY = Math.abs(deltaY);

    // Double-tap detection (small movement, quick tap)
    if (absDeltaX < 10 && absDeltaY < 10 && elapsed < 300) {
      const now = Date.now();
      if (now - lastTapRef.current < 300) {
        triggerLoveAnimation();
        lastTapRef.current = 0;
        return;
      }
      lastTapRef.current = now;
    }

    // Horizontal swipe (must be > 50px and predominantly horizontal)
    if (absDeltaX > 50 && absDeltaX > absDeltaY) {
      if (deltaX < 0) goToNext();  // swipe left = next
      else goToPrev();             // swipe right = prev
      return;
    }

    // Vertical swipe down to dismiss (> 100px, predominantly vertical)
    if (deltaY > 100 && absDeltaY > absDeltaX) {
      handleClose();
    }
  }, [goToNext, goToPrev, handleClose, triggerLoveAnimation]);

  if (isLoading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black" role="status" aria-label={t('loading')}>
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-600 border-t-white" />
      </div>
    );
  }

  if (!media) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black">
        <div className="text-center text-white">
          <p>{t('notFound')}</p>
          <button onClick={handleClose} className="mt-2 text-sm text-gray-400 hover:text-white">
            {t('goBack')}
          </button>
        </div>
      </div>
    );
  }

  const isAdminOrOwner = media.viewerRole === 'admin' || media.viewerRole === 'owner';
  const isContributorPlus = isAdminOrOwner || media.viewerRole === 'contributor';
  const canDelete = media.uploadedBy === user?.id || isAdminOrOwner;
  const canEditCaption = media.uploadedBy === user?.id || isAdminOrOwner;
  const canRotate = (media.uploadedBy === user?.id || isAdminOrOwner) && isContributorPlus;

  return (
    <div className="fixed inset-0 z-50 flex flex-col md:flex-row bg-black">
      {/* Photo area */}
      <div className="relative flex-1 min-h-0">
        {/* Top bar with frosted blur */}
        <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between bg-gradient-to-b from-black/70 to-transparent px-4 py-3 backdrop-blur-sm">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleClose}
            className="h-10 w-10 rounded-full text-white hover:bg-white/10"
            aria-label={t('actions.close')}
          >
            <X className="h-6 w-6" />
          </Button>
          <div className="flex items-center gap-2">
            {mediaIds && currentIndex >= 0 && (
              <span className="font-mono text-xs text-white/80" aria-label={t('position', { index: currentIndex + 1, total: mediaIds.length })}>
                {t('position', { index: currentIndex + 1, total: mediaIds.length })}
              </span>
            )}
            {media?.accessGroupId && (
              <span className="flex items-center gap-1 rounded-full bg-amber-500/20 px-2 py-0.5 text-xs text-amber-300 ring-1 ring-amber-400/30">
                <Lock className="h-3 w-3" />
                {t('restricted')}
              </span>
            )}
          </div>
          <div className="flex gap-1">
            {isAdminOrOwner && fullAccessGroup && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  const newGroupId = media.accessGroupId ? null : fullAccessGroup.id;
                  updateMediaMutation.mutate({ accessGroupId: newGroupId });
                }}
                disabled={updateMediaMutation.isPending}
                className={`h-10 w-10 rounded-full text-white hover:bg-white/10 ${media.accessGroupId ? 'text-amber-300' : ''}`}
                aria-label={media.accessGroupId ? t('actions.unrestrict') : t('actions.restrict')}
              >
                <Lock className="h-5 w-5" />
              </Button>
            )}
            {canRotate && media.type !== 'video' && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  const next = ((media.rotation || 0) + 90) % 360;
                  rotateMutation.mutate(next);
                }}
                disabled={rotateMutation.isPending}
                className="h-10 w-10 rounded-full text-white hover:bg-white/10"
                aria-label={t('actions.rotate')}
              >
                <RotateCw className="h-6 w-6" />
              </Button>
            )}
            {media.canDownload && media.fullUrl && media.type !== 'video' && (
              <Button
                variant="ghost"
                size="icon"
                onClick={async () => {
                  try {
                    const res = await fetch(media.fullUrl!);
                    const blob = await res.blob();
                    const dateStr = new Date(media.takenAt || media.createdAt).toISOString().split('T')[0];
                    const slug = media.caption ? media.caption.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '').slice(0, 30) : media.id.slice(0, 8);
                    const filename = `wovendays-${dateStr}-${slug}.jpg`;
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = filename;
                    a.click();
                    URL.revokeObjectURL(url);
                  } catch {
                    // silently fail
                  }
                }}
                className="h-10 w-10 rounded-full text-white hover:bg-white/10"
                aria-label={t('actions.download')}
              >
                <Download className="h-6 w-6" />
              </Button>
            )}
            {canDelete && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  if (confirm(t('actions.deleteConfirm'))) {
                    deleteMutation.mutate();
                  }
                }}
                disabled={deleteMutation.isPending}
                className="h-10 w-10 rounded-full text-white hover:bg-white/10"
                aria-label={t('actions.delete')}
              >
                <Trash2 className="h-6 w-6" />
              </Button>
            )}
          </div>
        </div>

        {/* Image with touch gestures and double-click */}
        <div
          className="flex h-full items-center justify-center p-4"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onDoubleClick={triggerLoveAnimation}
        >
          {media.type === 'video' ? (
            <VideoPlayer
              src={media.fullUrl ?? ''}
              poster={media.gridUrl ?? undefined}
              className="max-h-full max-w-full"
            />
          ) : (
            <img
              src={media.fullUrl ?? ''}
              alt={media.caption || ''}
              className="max-h-full max-w-full object-contain transition-transform duration-300"
              style={{
                touchAction: 'pinch-zoom',
                transform: media.rotation ? `rotate(${media.rotation}deg)` : undefined,
              }}
            />
          )}
          {showHeartAnimation && (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <Heart
                className="h-24 w-24 fill-red-500 text-red-500"
                style={{ animation: 'heartPop 800ms ease-out forwards' }}
              />
            </div>
          )}
        </div>

        {/* Prev/Next buttons — visible on hover-capable devices */}
        {hasPrev && (
          <button
            onClick={goToPrev}
            className="photo-nav-btn absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/40 p-3 text-white hover:bg-black/60 transition-colors"
            aria-label={t('actions.previous')}
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
        )}
        {hasNext && (
          <button
            onClick={goToNext}
            className="photo-nav-btn absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/40 p-3 text-white hover:bg-black/60 transition-colors"
            aria-label={t('actions.next')}
          >
            <ChevronRight className="h-6 w-6" />
          </button>
        )}

        {/* Bottom bar with frosted blur */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-4 py-4 backdrop-blur-sm">
          {editingCaption ? (
            <div className="mb-2 flex items-center gap-2">
              <Input
                type="text"
                value={captionDraft}
                onChange={(e) => setCaptionDraft(e.target.value)}
                placeholder={t('caption.placeholder')}
                maxLength={1000}
                className="flex-1 bg-white/10 border-white/20 text-white placeholder:text-white/50"
                aria-label={t('caption.edit')}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    captionMutation.mutate(captionDraft.trim() || null);
                  }
                  if (e.key === 'Escape') {
                    setEditingCaption(false);
                  }
                }}
              />
              <Button
                size="icon"
                variant="ghost"
                onClick={() => captionMutation.mutate(captionDraft.trim() || null)}
                disabled={captionMutation.isPending}
                className="h-8 w-8 rounded-full text-white hover:bg-white/10"
                aria-label={t('caption.save')}
              >
                <Check className="h-4 w-4" />
              </Button>
              <Button
                size="icon"
                variant="ghost"
                onClick={() => setEditingCaption(false)}
                className="h-8 w-8 rounded-full text-white hover:bg-white/10"
                aria-label={t('caption.cancel')}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <div className="mb-2 group/caption flex items-center gap-2">
              {media.caption ? (
                <p className="font-serif text-lg italic text-white">{media.caption}</p>
              ) : canEditCaption ? (
                <p className="font-serif text-sm italic text-white/50">{t('caption.empty')}</p>
              ) : null}
              {canEditCaption && (
                <button
                  onClick={() => {
                    setCaptionDraft(media.caption || '');
                    setEditingCaption(true);
                  }}
                  className="opacity-0 group-hover/caption:opacity-100 focus:opacity-100 rounded p-1 text-white/60 hover:text-white transition-opacity"
                  aria-label={t('caption.edit')}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          )}
          <div className="flex items-center gap-2 font-mono text-xs text-gray-300">
            <span>{media.uploader.name}</span>
            <span>·</span>
            {editingDate ? (
              <span className="flex items-center gap-1">
                <input
                  type="date"
                  value={dateDraft}
                  onChange={(e) => setDateDraft(e.target.value)}
                  className="bg-white/10 border border-white/20 rounded px-1.5 py-0.5 text-white text-xs font-mono"
                  aria-label={t('date.edit')}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const ms = dateDraft ? new Date(dateDraft + 'T12:00:00').getTime() : null;
                      dateMutation.mutate(ms);
                    }
                    if (e.key === 'Escape') setEditingDate(false);
                  }}
                />
                <button
                  onClick={() => {
                    const ms = dateDraft ? new Date(dateDraft + 'T12:00:00').getTime() : null;
                    dateMutation.mutate(ms);
                  }}
                  className="text-white/60 hover:text-white"
                  aria-label={t('date.save')}
                >
                  <Check className="h-3 w-3" />
                </button>
                <button
                  onClick={() => setEditingDate(false)}
                  className="text-white/60 hover:text-white"
                  aria-label={t('date.cancel')}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ) : (
              <span className="group/date inline-flex items-center gap-1">
                <span>
                  {new Date(media.takenAt || media.createdAt).toLocaleDateString(undefined, {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </span>
                {canEditCaption && (
                  <button
                    onClick={() => {
                      const d = new Date(media.takenAt || media.createdAt);
                      setDateDraft(d.toISOString().split('T')[0]!);
                      setEditingDate(true);
                    }}
                    className="opacity-0 group-hover/date:opacity-100 focus:opacity-100 text-white/60 hover:text-white transition-opacity"
                    aria-label={t('date.edit')}
                  >
                    <Calendar className="h-3 w-3" />
                  </button>
                )}
              </span>
            )}
            {media.latitude != null && media.longitude != null && (
              <>
                <span>·</span>
                <a
                  href={`https://maps.google.com/?q=${media.latitude},${media.longitude}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-gray-300 hover:text-white transition-colors"
                >
                  <MapPin className="h-3 w-3" />
                  <span>{t('location.view')}</span>
                </a>
              </>
            )}
          </div>
          <div className="mt-2 flex items-center gap-2">
            <button
              onClick={() => toggleLove(userLoved)}
              className={`flex items-center gap-1 rounded-full px-3 py-1.5 text-sm transition-colors ${
                userLoved
                  ? 'bg-red-500/20 ring-1 ring-red-400/40'
                  : 'bg-white/10 hover:bg-white/20'
              }`}
              aria-label={userLoved ? t('love.remove') : (media.type === 'video' ? t('love.addVideo') : t('love.add'))}
            >
              <Heart className={`h-4 w-4 ${userLoved ? 'fill-red-500 text-red-500' : 'text-white'}`} />
              {loveCount > 0 && <span className="font-mono text-xs text-white">{loveCount}</span>}
            </button>
            <ReactionBar mediaId={media.id} />
          </div>
          {/* Child tag chips — keep bg-primary-600 class (E2E tests assert on it) */}
          {availableChildren.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {availableChildren.map((child) => {
                const tagged = currentTagIds.has(child.id);
                return (
                  <button
                    key={child.id}
                    onClick={() => handleToggleChild(child.id)}
                    disabled={tagMutation.isPending}
                    className={`rounded-full px-3 py-1 text-xs font-medium transition-colors disabled:opacity-50 ${
                      tagged
                        ? 'bg-primary-600 text-white'
                        : 'bg-white/20 text-white hover:bg-white/30'
                    }`}
                  >
                    {child.name}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Comment sidebar — always visible */}
      <div className="max-h-[40vh] md:max-h-none md:w-96 md:border-l md:border-white/10 bg-background" aria-label={t('comments')}>
        <CommentSheet mediaId={media.id} familyId={media.familyId} viewerRole={media.viewerRole} />
      </div>
    </div>
  );
}
